import { ShieldAlert } from "lucide-react";

/**
 * Terms & Conditions — governs the researcher/buyer contract for every order
 * placed through nebulaaminos.com. Links live in the footer and are referenced
 * from the checkout attestation. Update TERMS_VERSION in Checkout.jsx whenever
 * the material terms change so past orders retain their signed version.
 */
export default function Terms() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16" data-testid="terms-page">
      <div className="eyebrow">Legal</div>
      <h1 className="h-display text-4xl md:text-6xl mt-2 leading-[1.04]">Terms &amp; Conditions.</h1>
      <p className="mt-4 text-[13px] font-mono uppercase tracking-[0.18em] text-[var(--ink-muted)]">
        Version 2026.02-rev2 · Effective February 1, 2026
      </p>

      <div className="mt-6 border border-[var(--line)] bg-[var(--paper-2)] rounded-md p-4 text-[var(--ink-soft)] text-sm leading-relaxed">
        These Terms &amp; Conditions ("Terms") form a binding agreement between you ("Buyer",
        "you", "Researcher") and Nebula Aminos ("we", "us", "Company") governing every purchase
        placed through nebulaaminos.com. By creating an account or placing an order, you accept
        these Terms in full.
      </div>

      <div className="mt-12 space-y-10 text-[var(--ink-soft)]">
        <Section title="1. Eligibility">
          <p>You must be 21 years of age or older and either an accredited laboratory, a research
          institution, an educational institution, a chemical reseller, a licensed business buyer,
          or a qualified independent researcher. Consumer purchases and purchases intended for
          personal, human, or veterinary use are prohibited.</p>
        </Section>

        <Section title="2. Research Use Only (RUO)">
          <p>All products are sold strictly for laboratory research use only. They are not drugs,
          dietary supplements, cosmetics, food additives, or household chemicals. They are not
          approved by the FDA for any human or veterinary use, and no product listed is intended
          to diagnose, treat, cure, or prevent any disease.</p>
          <div className="mt-4 border border-[var(--line)] bg-[var(--paper-2)] rounded-md p-4 flex items-start gap-3">
            <ShieldAlert className="h-5 w-5 text-[var(--terra)] shrink-0 mt-0.5" />
            <p className="text-sm">By purchasing, you certify you will not administer these
            materials to humans or animals, will not resell them for human or veterinary use, and
            accept responsibility for compliance with all applicable federal, state, and local
            regulations.</p>
          </div>
        </Section>

        <Section title="3. Order acceptance &amp; pricing">
          <p>All orders are offers to purchase and remain subject to acceptance by Nebula Aminos.
          We may cancel, refuse, or limit orders at our sole discretion — including for stock
          issues, pricing errors, compliance flags, or failed identity verification. Pricing is
          fixed on-site in USD; sales tax is not collected. Descriptor on your card or bank
          statement will read <span className="font-mono text-[var(--ink)]">NEBULA AMINOS RUO</span>.</p>
        </Section>

        <Section title="4. Payment">
          <p>We accept credit/debit card via VERIFIED Crypto Checkout (hosted, PCI-DSS compliant)
          and native cryptocurrency (BTC, ETH, USDC-Polygon). Card orders settle to us in USDC on
          Polygon. Orders may be placed into a brief compliance review queue prior to payment
          capture; you will not be charged until your order is released.</p>
        </Section>

        <Section title="5. Shipping &amp; delivery">
          <p>Orders ship via USPS, UPS, or FedEx to U.S. addresses only. Free ground
          shipping applies to orders over $199.99. Nebula Aminos does not ship internationally at
          this time. Full details, cutoffs, and cold-chain handling are described in our{" "}
          <a href="/shipping-policy" className="underline underline-offset-2 hover:text-[var(--terra)]">
            Shipping Policy
          </a>.</p>
        </Section>

        <Section title="6. Returns, refunds &amp; damaged shipments">
          <p>Governed by our{" "}
          <a href="/refund-policy" className="underline underline-offset-2 hover:text-[var(--terra)]">
            Return &amp; Refund Policy
          </a>. In short: unopened items may be returned within 14 days for store credit;
          shipping damage is fully refunded with photo evidence; opened or reconstituted vials
          are non-refundable for safety and chain-of-custody reasons.</p>
        </Section>

        <Section title="7. Chargebacks">
          <p>You agree to contact us first before initiating a chargeback. Fraudulent or
          unwarranted chargebacks (including "friendly fraud" — receipt of goods followed by a
          dispute) will be defended with the buyer attestation, the shipping proof-of-delivery,
          and the RUO acknowledgment logged at checkout, and may be referred to collections.</p>
        </Section>

        <Section title="8. Warranties &amp; disclaimers">
          <p>We warrant that products conform to the identity and purity stated on their
          batch-specific Certificate of Analysis at the time of shipment. TO THE MAXIMUM EXTENT
          PERMITTED BY LAW, PRODUCTS ARE OTHERWISE PROVIDED "AS IS", AND NEBULA AMINOS DISCLAIMS
          ALL OTHER WARRANTIES, EXPRESS OR IMPLIED, INCLUDING FITNESS FOR A PARTICULAR PURPOSE
          AND MERCHANTABILITY.</p>
        </Section>

        <Section title="9. Limitation of liability">
          <p>To the maximum extent permitted by law, Nebula Aminos' aggregate liability arising
          out of any product or transaction shall not exceed the amount you paid for the specific
          product giving rise to the claim. We are not liable for any indirect, incidental,
          special, consequential, or punitive damages, including loss of research data, lost
          time, or lost profits.</p>
        </Section>

        <Section title="10. Indemnification">
          <p>You agree to indemnify and hold harmless Nebula Aminos, its officers, employees, and
          agents from any claim, damage, loss, liability, or expense (including reasonable
          attorneys' fees) arising from (a) your breach of these Terms, (b) your misuse of any
          product, or (c) any administration of any product to a human or animal.</p>
        </Section>

        <Section title="11. Governing law &amp; venue">
          <p>These Terms are governed by the laws of the State of Utah, without regard to its
          conflict of law provisions. Exclusive venue for any dispute is the state or federal
          courts located in Salt Lake County, Utah, and you consent to personal jurisdiction
          there.</p>
        </Section>

        <Section title="12. Changes to these Terms">
          <p>We may update these Terms from time to time. The version in force at the time your
          order is placed is the one that governs your order and is recorded with the order for
          audit. Continued use of the site after a change constitutes acceptance of the updated
          Terms.</p>
        </Section>

        <Section title="13. Contact">
          <p>Questions about these Terms: <a className="underline underline-offset-2 hover:text-[var(--terra)]" href="mailto:support@nebulaaminos.com">support@nebulaaminos.com</a> · 305-877-7739 · 113 W Ryker Ln, Midvale UT 84047, USA.</p>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display text-2xl font-medium text-[var(--ink)]">{title}</h2>
      <div className="mt-3 leading-relaxed text-[var(--ink-soft)] space-y-3">{children}</div>
    </section>
  );
}
